/**
 * Pre-flight failsafe doctrine endpoints.
 *
 * A doctrine is a set of autopilot FS_* parameters deciding how an aircraft
 * fails on link loss, GPS loss, and low battery (see
 * api/app/routers/failsafe.py). Unlike droneCommands, these do NOT route
 * through unwrapCommand: the server sends its own multi-parameter envelope
 * ({ok, applied[], failed}), not a bare CommandResult, and a partial apply is
 * a result the UI must render field by field, not fold into one error. A
 * refused apply (dangerous doctrine without confirm, armed aircraft, unknown
 * doctrine) still arrives as an HTTP error through the client's normal
 * ApiResult failure path.
 */

import { api, type ApiResult } from '@/api/client';
import type {
  DroneId,
  FailsafeApplyResult,
  FailsafeInfo,
  FleetFailsafeResult,
} from '@/types/domain';

/** List available doctrines and the one read back off the aircraft's FS_*
 * parameters, if any (see `FailsafeInfo.currentDetail`). */
export function getFailsafe(id: DroneId): Promise<ApiResult<FailsafeInfo>> {
  return api.get<FailsafeInfo>(`/drones/${encodeURIComponent(id)}/failsafe`, { logged: false });
}

/**
 * Apply a doctrine to one aircraft. `confirm` must be true for a doctrine that
 * disables the return-to-operator failsafes, or the server answers 400. An
 * armed aircraft answers 409; an unknown doctrine 422.
 */
export function applyFailsafe(
  id: DroneId,
  doctrine: string,
  confirm: boolean,
): Promise<ApiResult<FailsafeApplyResult>> {
  return api.put<FailsafeApplyResult>(
    `/drones/${encodeURIComponent(id)}/failsafe`,
    { doctrine, confirm },
    { label: `FAILSAFE ${doctrine} ${id}` },
  );
}

/**
 * Apply a doctrine across every drone the caller can control. The server
 * scopes the fleet by accessible_drones; a drone the caller cannot see never
 * appears in the results at all.
 */
export function applyFleetFailsafe(
  doctrine: string,
  confirm: boolean,
): Promise<ApiResult<FleetFailsafeResult>> {
  return api.post<FleetFailsafeResult>(
    '/drones/failsafe/fleet',
    { doctrine, confirm },
    { label: `FAILSAFE FLEET ${doctrine}` },
  );
}
